import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable, Subject } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { map, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialogComponent, IConfirmDialog } from '@shared/components/confirm-dialog/confirm-dialog.component';

@Injectable({ providedIn: 'root' })
export class SharedService {
  public mobile: boolean = false;
  public tablet: boolean = false;
  public development: boolean = environment.build === 'Development';
  private api: string = environment.privateApi;
  private changeTheme: Subject<string> = new Subject<string>();
  public darkMode: boolean = false;
  public theme: string = 'light';

  /**
   * Color Codes
   */
  public purple: string = '#9355dc';
  public blue: string = '#628ef4';
  public gold: string = '#ffbe2e';
  public green: string = '#70e17b';
  public pink: string = '#e9695f';
  public orange: string = '#EC891D';
  public red: string = '#fc4132';
  public loading: boolean = true;
  public today: Date = new Date(Date.now());

  /**
   * License Types
   */
  public individualLicense: string = 'IndividualLicense';
  public individualApplicant: string = 'IndividualApplicant';
  public entityLicense: string = 'EntityLicense';
  public entityApplicant: string = 'EntityApplicant';

  /*
   * Statuses
   */
  public started: string = "Started";
  public submitted: string = "Submitted";
  public submittedForApproval: string = 'Submitted for Approval';
  public resubmitted: string = "Resubmitted";
  public awaitingPayment: string = "Approved, Pending Payment and Clearance";
  public approved: string = "Approved";
  public incomplete: string = "Incomplete";
  public insufficient: string = "Insufficient";
  public underReview: string = "Under Review";
  public denied: string = "Denied";
  public revoked: string = "Revoked";
  public expired: string = "Expired";
  public withdrawn: string = "Withdrawn";
  public surrendered: string = "Surrendered";
  public closed: string = 'Closed';

  stateList: string[] = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
    "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
    "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
    "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "American Samoa" ,
    "Commonwealth of the Northern Mariana Islands", "Guam", "Puerto Rico", "US Virgin Islands",
    "Federated States of Micronesia", "Republic of the Marshall Islands", "Republic of Palau"
  ];

  countyList: string[] = [
    "Baker", "Benton", "Clackamas", "Clatsop", "Columbia", "Coos", "Crook",
    "Curry", "Deschutes", "Douglas", "Gilliam", "Grant", "Harney", "Hood River",
    "Jackson", "Jefferson", "Josephine", "Klamath", "Lake", "Lane", "Lincoln",
    "Linn", "Malheur", "Marion", "Morrow", "Multnomah", "Polk", "Sherman",
    "Tillamook", "Umatilla", "Union", "Wallowa", "Wasco", "Washington", "Wheeler",
    "Yamhill"
  ];

  loremIpsum: string = `Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
  incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco
  laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit
  esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt
  in culpa qui officia deserunt mollit anim id est laborum.`

  public confirmDialogRef: MatDialogRef<ConfirmDialogComponent>;

  private httpClient = inject(HttpClient);
  public dialog = inject(MatDialog);
  public toastr = inject(ToastrService);

  public openNotice(message: string): void {
    let data: IConfirmDialog = {
      message: message,
      confirmation: false
    }
    this.openDialog(data);
  }

  public openConfirm(message: string): void {
    let data: IConfirmDialog = {
      message: message,
      confirmation: true
    }
    this.openDialog(data);
  }

  openDialog(data: IConfirmDialog): void {
    this.confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: data,
      role: 'dialog',
      ariaLabel: "Confirmation Dialog",
      minWidth: '300px',
      maxWidth: !this.mobile? '700px' : '300px',
      maxHeight: this.mobile? '600px' : '700px',
      panelClass: this.theme === "dark" ? "theme-dark" : "",
    });
  }

  public confirmed(): Observable<any>{
    return this.confirmDialogRef.afterClosed().pipe(take(1), map(response => {
      return response;
    }));
  }

  get isMobile(): boolean {
    return this.mobile;
  }

  get counties(): string[] {
    return this.countyList;
  }

  get states(): string[] {
    return this.stateList;
  }

  themeChanged$ = this.changeTheme.asObservable();

  getUserTheme(): void {
    this.httpClient.get<string>(this.api + "UserProfile/GetUserTheme", { responseType: 'text' as 'json' }).subscribe({
      next: response => {
        this.theme = response;
        this.toggleTheme();
      },
      error: e => console.log('error getting user theme', e)
    });
  }

  updateUserTheme(): void {
    this.httpClient.post<any>(this.api + "UserProfile/UpdateUserTheme/" + this.theme, null).subscribe(
      () => { },
      error => console.log('error', error)
    );
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
    this.toastr.success("Copied!");
  }

  toggleTheme() {
    this.darkMode = this.theme === 'dark';
    this.changeTheme.next(this.theme);
  }

  consoleLog(error: any): void {
    if(this.development){
      console.log('error', error);
    }
  }

  isCancelled(response: any): boolean {
    return response === 'cancel' || response === null || response === '' || response === undefined;
  }

  getDateFromString(date: string): Date {
    const dateComponents = date.split('/');
    const month = parseInt(dateComponents[0], 10);
    const day = parseInt(dateComponents[1], 10);
    const year = parseInt(dateComponents[2], 10);
    return new Date(year, month - 1, day);
  }
}
